import type { ReactNode } from "react";

import { Trans } from "@lingui/react/macro";
import { Filter as FilterIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { DataTableColumn, DataTableInstance } from "../table-features";
import type { FilterField } from "./types";

import { cn } from "../../../../../utils/shadcn-ui";
import { Popover, PopoverContent, PopoverTrigger } from "../../../base/popover";
import { CountBadge } from "../../../count-badge";
import { ColumnVisibilityPanel } from "./column-visibility-panel";
import { FilterFieldEditor } from "./filter-field-editor";
import { GROUP_HEADING_CLASS } from "./styles";
import { clearAll, getActiveCount } from "./tanstack-bridge";
import { usePinScrollToTop } from "./use-pin-scroll-to-top";

type Tab = "filters" | "columns";

// Shared by the two tab buttons and the static single-panel label.
const TAB_CLASS =
  "px-3 py-2 text-center text-xs font-bold uppercase tracking-wider transition-colors";

export function FilterDialogPopover<T>({
  table,
  fields,
  columns,
  disabled,
}: {
  table: DataTableInstance<T>;
  fields: FilterField<T>[];
  /** The columns the Columns tab lists — see `toggleableColumns`. */
  columns: DataTableColumn<T>[];
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const activeCount = getActiveCount(table, fields);
  const hasActive = activeCount > 0;

  const hasFilters = fields.length > 0;
  const hasColumns = columns.length > 0;
  const hasHidden = columns.some((column) => !column.getIsVisible());
  const defaultTab: Tab = hasFilters ? "filters" : "columns";
  const [tab, setTab] = useState<Tab>(defaultTab);
  // Every open lands on the default tab rather than resuming wherever the last
  // visit left off.
  useEffect(() => {
    if (open) setTab(defaultTab);
  }, [open, defaultTab]);
  const showReset = tab === "filters" ? hasActive : hasHidden;

  const groups: { name: string; fields: FilterField<T>[] }[] = [];
  const groupIndex = new Map<string, number>();
  for (const field of fields) {
    let i = groupIndex.get(field.group);
    if (i === undefined) {
      i = groups.length;
      groupIndex.set(field.group, i);
      groups.push({ name: field.group, fields: [] });
    }
    groups[i].fields.push(field);
  }

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Restarted on tab switch too, so each tab starts at the top rather than
  // inheriting the other's scroll position.
  usePinScrollToTop(
    scrollContainerRef,
    open,
    '[data-slot="popover-content"]',
    tab,
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "relative inline-flex items-center gap-1.5 py-1.5 px-3 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap border bg-transparent",
            disabled
              ? "border-dashed border-input text-muted-foreground cursor-not-allowed opacity-50"
              : hasActive
                ? "border-solid border-brand-primary text-brand-primary hover:bg-brand-primary/10 cursor-pointer"
                : "border-dashed border-input text-foreground hover:border-brand-primary hover:text-brand-primary cursor-pointer",
            !disabled && hasActive && open && "bg-brand-primary/10",
            !disabled &&
              !hasActive &&
              open &&
              "border-brand-primary text-brand-primary",
          )}
        >
          <FilterIcon size={14} fill={hasActive ? "currentColor" : "none"} />
          <Trans>Filter</Trans>
          <CountBadge count={activeCount} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        // `overflow-hidden` so the square-cornered tab header is clipped to the
        // panel's rounded corners instead of poking out past them.
        className="relative p-0 overflow-hidden w-80"
      >
        {hasFilters && hasColumns ? (
          <div className="flex w-full border-b">
            <TabButton
              active={tab === "filters"}
              onClick={() => setTab("filters")}
            >
              <Trans>Filters</Trans>
            </TabButton>
            {/* Divider between the two halves, drawn on the button so it spans
                the full row height rather than just the text. */}
            <TabButton
              active={tab === "columns"}
              onClick={() => setTab("columns")}
              className="border-l"
            >
              <Trans>Columns</Trans>
            </TabButton>
          </div>
        ) : (
          <div
            className={cn(
              TAB_CLASS,
              "border-b text-muted-foreground/80 cursor-default",
            )}
          >
            {hasFilters ? <Trans>Filters</Trans> : <Trans>Columns</Trans>}
          </div>
        )}
        <div
          ref={scrollContainerRef}
          // The footer is pinned over this area, so its strip is reserved for
          // as long as it's showing — otherwise the last row would sit behind
          // it. While the list scrolls (capped at 60vh) that padding grows only
          // the scroll content, leaving the dialog's own height untouched.
          className={cn(
            "max-h-[60vh] overflow-y-auto",
            showReset ? "pb-9" : "pb-2",
          )}
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {tab === "filters" ? (
            groups.map((group) => (
              <div key={group.name}>
                <div className={GROUP_HEADING_CLASS}>{group.name}</div>
                {group.fields.map((field) => (
                  <FilterFieldEditor
                    key={field.id}
                    table={table}
                    field={field}
                    onSubmit={() => setOpen(false)}
                  />
                ))}
              </div>
            ))
          ) : (
            <ColumnVisibilityPanel table={table} columns={columns} />
          )}
        </div>
        {/* Pinned over the bottom of the list, out of the flow, so a scrolling
            list doesn't grow the dialog when this appears. Mirrors the header —
            centred, full width — because it acts on the whole panel, unlike the
            per-field "Clear selection". */}
        {showReset && (
          <button
            type="button"
            onClick={
              tab === "filters"
                ? () => clearAll(table, fields)
                : // Not `toggleAllColumnsVisible` — that would also reveal the
                  // filter-only (`hidden`) columns, which must never render.
                  () =>
                    table.setColumnVisibility((prev) => {
                      const next = { ...prev };
                      for (const column of columns) next[column.id] = true;
                      return next;
                    })
            }
            className="absolute inset-x-0 bottom-0 rounded-b-xl border-t bg-popover px-3 py-2 text-xs font-semibold text-muted-foreground transition-colors cursor-pointer hover:bg-muted hover:text-brand-primary"
          >
            {tab === "filters" ? (
              <Trans>Clear all filters</Trans>
            ) : (
              <Trans>Show all columns</Trans>
            )}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * A plain button rather than a Radix `TabsTrigger`: the active tab is tracked in
 * `useState` and marked by its text colour alone, so a tablist would only add
 * `aria-controls` pointing at panels that don't exist as separate elements.
 * `flex-1` splits the header in half between the two.
 */
function TabButton({
  active,
  onClick,
  className,
  children,
}: {
  active: boolean;
  onClick: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        TAB_CLASS,
        "flex-1 cursor-pointer",
        active
          ? "text-brand-primary"
          : "text-muted-foreground hover:text-brand-primary",
        className,
      )}
    >
      {children}
    </button>
  );
}
